/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IFullDeviceDetails } from "../../store/api/deviceConfig/types";
import { getSelectedIndex, getConfigRoutesByNodeType } from "./helpers";

import { IRouteInfo } from "../../globals/globals";
import { configCountsSelector } from "../../store/api/deviceConfig/selectors";
import { useSelector } from "react-redux";
import { isLoadingStatusSelector } from "../../store/helpers/helperSelectors";
import { Badge } from "../../components/ui/badge";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import { useGetDasFeatureFlagsQuery } from "../../store/api/devices/deviceApiSlice";
import { isDasModel } from "./helpers";
import { getBaseDomain } from "../../utils/cookie";

interface Iprops {
  deviceDetail: IFullDeviceDetails;
  sxProps?: any;
}

const ConfigGroupList: React.FC<Iprops> = ({ deviceDetail }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedIndex, setSelectedIndex] = React.useState(getSelectedIndex(location));
  const configCounts = useSelector(configCountsSelector);
  const loadingStatus = useSelector(isLoadingStatusSelector);
  const permissions = useSelector(selectProfilePermissionsSelector);

  const isDas = isDasModel(deviceDetail?.modelName);
  const { data: featureFlags } = useGetDasFeatureFlagsQuery(deviceDetail?.modelName, {
    skip: !isDas || !deviceDetail?.modelName,
  });
  const inventoryDiscoveryEnabled = isDas ? (featureFlags?.inventoryDiscovery ?? true) : true;

  const configRoutes = getConfigRoutesByNodeType(
    deviceDetail.nodeType,
    deviceDetail.swVersion,
    deviceDetail?.modelName,
    deviceDetail?.configurationStatus,
    inventoryDiscoveryEnabled,
  );

  const handleListItemClick = (index: number, navigatePath: string, item: IRouteInfo) => {
    if (item.isExternal) {
      const ip = deviceDetail?.ipAddress;
      const baseDomain = getBaseDomain();
      const deviceId = deviceDetail?.objectKey;
      // Route the DAS GUI through the auth-gated proxy-<deviceId> subdomain so the
      // device IP/port is never exposed. Fall back to a direct link when there is
      // no usable base domain (e.g. IP/localhost dev hosts).
      if (baseDomain && deviceId) {
        window.open(`${window.location.protocol}//proxy-${deviceId}.${baseDomain}/`, "_blank");
      } else if (ip) {
        window.open("https://" + ip.replace(/^\[|\]$/g, ""), "_blank");
      }
      return;
    }
    setSelectedIndex(index);
    navigate(navigatePath, { state: { deviceDetail } });
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (location) {
      setSelectedIndex(getSelectedIndex(location));
    }
  }, [location]);

  return (
    <nav className="flex flex-col gap-0.5" aria-label="devices count">
      {configRoutes?.length > 0 &&
        configRoutes?.map((item: IRouteInfo, index: number) => {
          const isActive = selectedIndex === item.Key;
          const showCount = index !== 0 && !["commissioning", "ssh", "performance", "files", "snmpdetails", "location", "hcm-info", "dcu-info", "controller-info", "das-topology", "web-terminal"].includes(item.Path);
          const isLoading = showCount && loadingStatus["deviceCounts"];
          const count = configCounts[item?.Path] || 0;
          const isWebTerminal = item.isExternal && item.Path === "web-terminal";
          const isWebTerminalDisabled = isWebTerminal && (!deviceDetail?.ipAddress || !permissions?.hasOpCodeGroup("manage_sn"));

          return (
            <button
              key={item.Key}
              type="button"
              onClick={() => handleListItemClick(item?.Key, item?.Path, item)}
              disabled={isWebTerminalDisabled}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isActive
                ? "bg-muted font-medium text-foreground"
                : "bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                } ${isWebTerminalDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <span className="truncate">{item.Text}</span>
              {isLoading ? (
                <span className="material-icons animate-spin text-muted-foreground shrink-0" style={{ fontSize: 16 }}>sync</span>
              ) : showCount ? (
                <Badge variant="secondary" className="shrink-0 h-5 min-w-[20px] px-1.5 text-xs tabular-nums justify-center">
                  {count}
                </Badge>
              ) : isWebTerminal ? (
                <span className="material-icons text-muted-foreground shrink-0" style={{ fontSize: 16 }}>open_in_new</span>
              ) : null}
            </button>
          );
        })}
    </nav>
  );
};

export default ConfigGroupList;
